import { useEffect, type ReactNode } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useMaintenanceCheck } from '@/hooks/useMaintenanceCheck';
import { FileText, Layers, Sparkles, BookOpen, Users, GraduationCap, Instagram, Facebook } from 'lucide-react';
import Maintenance from '@/pages/Maintenance';
import { LoadingAnimation } from '@/components/LoadingAnimation';
import { LandingHeader } from '@/components/landing/LandingHeader';
import { HeroSection } from '@/components/landing/HeroSection';
import { FeaturesSection } from '@/components/landing/FeaturesSection';
import { Reveal } from '@/components/landing/Reveal';
import footerLogo from '@/assets/logo-refify-white.webp';
import { Seo } from '@/components/Seo';
import { FaqSection, faqItems } from '@/components/landing/FaqSection';

import mascotPencilRun from '@/assets/landing/mascot-pencil-run.png.asset.json';
import mascotCup from '@/assets/landing/mascot-cup.png.asset.json';
import mascotCloud from '@/assets/landing/mascot-cloud.png.asset.json';
import mascotPencil from '@/assets/landing/mascot-pencil.png.asset.json';
import mascotPaper from '@/assets/landing/mascot-paper.png.asset.json';
import screenshotApp from '@/assets/landing/screenshot-app.png.asset.json';
import screenshotSearchFill from '@/assets/landing/screenshot-search-fill.png.asset.json';
import stylesPills from '@/assets/landing/styles-pills.png.asset.json';
import templateBuilderGraphic from '@/assets/landing/template-builder-graphic.png.asset.json';
import iconStudents from '@/assets/landing/icon-students.png.asset.json';
import iconResearchers from '@/assets/landing/icon-researchers.png.asset.json';
import iconInstitutions from '@/assets/landing/icon-institutions.png.asset.json';
import iconCreators from '@/assets/landing/icon-creators.png.asset.json';
import videoPaperReferences from '@/assets/landing/video-paper-references.png.asset.json';
import videoFavourites from '@/assets/landing/video-favourites.png.asset.json';
import videoShareCollaborate from '@/assets/landing/video-share-collaborate.png.asset.json';
import videoDiscover from '@/assets/landing/video-discover.png.asset.json';
import videoExport from '@/assets/landing/video-export.png.asset.json';
import videoTemplateBuilder from '@/assets/landing/video-template-builder.png.asset.json';

interface LandingBandProps {
  sage?: boolean;
  image: string;
  imageAlt: string;
  imageSide?: 'left' | 'right';
  imageClassName?: string;
  title: ReactNode;
  body: string;
  black?: boolean;
  bodySize?: 'lg' | 'md';
  titleClassName?: string;
}

function LandingBand({
  sage = false,
  image,
  imageAlt,
  imageSide = 'left',
  imageClassName = 'max-w-sm',
  title,
  body,
  black = false,
  bodySize = 'lg',
  titleClassName = 'lg:max-w-[48vw]',
}: LandingBandProps) {
  return (
    <section className={`relative overflow-hidden ${sage ? 'bg-landing-sage' : 'bg-white'}`}>
      {/* Giant soft circle anchored to the left viewport edge — only a smooth arc
          enters the band, clipped by the section's overflow-hidden. On mobile and
          tablet the circle is 2.5x larger (and pushed further off-screen so the arc
          depth stays the same), giving a much softer, wider sweep behind the stacked
          content. Desktop (lg and up) keeps the exact Figma measurements. */}
      {sage && (
        <div
          aria-hidden
          className="pointer-events-none absolute left-0 top-1/2 -translate-x-[70%] -translate-y-1/2 w-[175vw] aspect-square rounded-full bg-white/75 lg:-translate-x-[38%] lg:w-[70vw]"
        />
      )}

      <div className="mx-auto max-w-[1920px] px-6 md:px-12 xl:px-24 py-16 md:py-24 lg:py-0 lg:min-h-[650px] lg:flex lg:items-center relative">
        <div
          className={`grid w-full gap-10 lg:gap-8 items-center ${
            imageSide === 'left'
              ? 'lg:grid-cols-[2fr_3fr]'
              : 'lg:grid-cols-[1.15fr_1fr] lg:[&>*:first-child]:order-2'
          }`}
        >
          <Reveal
            variant={imageSide === 'left' ? 'slide-right' : 'slide-left'}
            duration={800}
            className={`relative flex justify-center ${
              imageSide === 'left' ? 'lg:justify-end' : 'lg:justify-start'
            }`}
          >
            <img
              src={image}
              alt={imageAlt}
              className={`relative w-full ${imageClassName} h-auto`}
              loading="lazy"
            />
          </Reveal>
          <Reveal variant={imageSide === 'left' ? 'slide-left' : 'slide-right'} delay={120} duration={800}>
            <div>
              <h2
                className={`landing-heading-halo ${
                  black ? 'font-black' : 'font-bold'
                } text-[clamp(2.75rem,5vw,6rem)] text-landing-green leading-[1.05] tracking-[-0.02em] mb-6 ${titleClassName}`}
              >
                {title}
              </h2>
              <p
                className={`text-landing-green whitespace-pre-line lg:max-w-[min(45vw,880px)] ${
                  bodySize === 'lg'
                    ? 'text-[clamp(1.375rem,2.1vw,2.5rem)] leading-[1.33]'
                    : 'text-[clamp(1.125rem,1.6vw,1.875rem)] leading-[1.77]'
                }`}
              >
                {body}
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function CtaSection({
  title,
  id,
  dark = false,
}: {
  title: string;
  id?: string;
  dark?: boolean;
}) {
  return (
    <section id={id} className={dark ? 'bg-landing-deep' : undefined}>
      <div className="container mx-auto px-6 py-16 md:py-24 text-center">
        <Reveal variant="scale" duration={800}>
          <h2
            className={`text-3xl md:text-4xl font-bold tracking-tight mb-8 ${
              dark ? 'text-white' : 'text-landing-green'
            }`}
          >
            {title}
          </h2>
        </Reveal>
        <Reveal variant="fade-up" delay={180} duration={700}>
          <Link
            to="/auth"
            className={`inline-flex items-center justify-center h-14 px-10 rounded-xl text-lg font-semibold transition-transform duration-300 hover:-translate-y-0.5 ${
              dark
                ? 'bg-white text-landing-green shadow-[0px_4px_12px_-3px_rgb(0_0_0_/_0.35)]'
                : 'bg-landing-green text-white shadow-[0px_4px_12px_-3px_rgb(44_150_115_/_0.3)]'
            }`}
          >
            Get started!
          </Link>
          <p className={`text-xs mt-4 ${dark ? 'text-white/90' : 'text-landing-green'}`}>
            By signing up, you agree to the{' '}
            <Link to="/terms" className="underline hover:opacity-70 transition-opacity">
              Terms and Conditions
            </Link>{' '}
            and{' '}
            <Link to="/privacy" className="underline hover:opacity-70 transition-opacity">
              Privacy Policy
            </Link>
            .
          </p>
        </Reveal>
      </div>
    </section>
  );
}

export default function Index() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();

  const { isLoading: maintenanceLoading, isMaintenanceActive, settings: maintenanceSettings } = useMaintenanceCheck();

  useEffect(() => {
    if (!loading && user) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  if (maintenanceLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <LoadingAnimation />
      </div>);
  }

  if (isMaintenanceActive) {
    return <Maintenance message={maintenanceSettings.message} endTime={maintenanceSettings.end_time} />;
  }

  const features = [{
    icon: FileText,
    title: 'Paper & References',
    desc: 'Create papers and keep track of your references by organising, annotating, and citing them in one place.',
    image: videoPaperReferences.url,
    video: '/videos/paper-reference-tutorial.mp4'
  }, {
    icon: Sparkles,
    title: 'Favourites',
    desc: 'Keep track of your favourite references and add them seamlessly to your papers.',
    image: videoFavourites.url,
    video: '/videos/favorites-tutorial.mp4'
  }, {
    icon: Users,
    title: 'Share & Collaborate',
    desc: 'Share papers, collaborate with peers, create lists, and control access levels.',
    image: videoShareCollaborate.url,
    video: '/videos/share-tutorial.mp4'
  }, {
    icon: BookOpen,
    title: 'Discover',
    desc: 'Create and share reference styles with the global community, or copy a shared collection and customise it to fit your needs.',
    image: videoDiscover.url,
    video: '/videos/discover-tutorial.mp4'
  }, {
    icon: GraduationCap,
    title: 'Export',
    desc: 'Export your references in multiple file formats, including Word, Rich Text, PDF, and more — while keeping your formatting intact.',
    image: videoExport.url,
    video: '/videos/export-tutorial.mp4'
  }, {
    icon: Layers,
    title: 'Template builder',
    desc: 'Create and customise your reference styles down to the very last detail, and control every punctuation with the template builder.',
    image: videoTemplateBuilder.url,
    video: '/videos/template-tutorial.mp4'
  }];

  const audience = [{
    icon: iconStudents.url,
    title: 'Students',
    desc: "Create citations quickly, accurately, and effortlessly with Refify. Choose from thousands of citation styles, or customise a ready-made style to match your university's referencing guidelines. Designed for students at every level, from beginners to advanced researchers.",
  }, {
    icon: iconResearchers.url,
    title: 'Researchers',
    desc: 'Organise and share references with your team. Create and refine citation styles to meet exact requirements. Built for precision and consistency.',
  }, {
    icon: iconInstitutions.url,
    title: 'Institutions/Universities',
    desc: 'Create and share interactive referencing guides with teams and students. Promote academic integrity, ensure consistency, and help students learn proper citation practices.',
  }, {
    icon: iconCreators.url,
    title: 'Content Creators',
    desc: 'Make your work look credible and well-researched. Stand out with accurate references.',
  }];

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const headerOffset = 88;
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - headerOffset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };

  const openExternalLink = (url: string) => (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const popup = window.open(url, '_blank', 'noopener,noreferrer');
    if (popup) {
      popup.opener = null;
    }
  };

  return <div className="min-h-screen bg-white force-light-theme overflow-x-clip">
      <Seo
        title="Refify – Interactive Referencing System"
        description="Refify helps you create, collect, organise, and cite sources for academic writing with customisable reference styles."
        path="/"
      />
      {/* Header */}
      <LandingHeader scrollToSection={scrollToSection} />

      {/* Hero */}
      <HeroSection scrollToSection={scrollToSection} />

      <main>
        {/* Who is Refify for */}
        <section className="container mx-auto px-6 py-16 md:py-24">
          <Reveal variant="fade-up" duration={800}>
            <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-landing-green tracking-tight">
              Who is Refify for?
            </h2>
          </Reveal>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10 max-w-6xl mx-auto">
            {audience.map((item, i) => (
              <Reveal key={item.title} variant="fade-up" delay={150 + i * 120} duration={750}>
                <div className="transition-transform duration-500 hover:-translate-y-1">
                  <img src={item.icon} alt="" className="h-16 w-auto mb-4" loading="lazy" />
                  <h3 className="text-xl font-bold mb-3 text-landing-green tracking-tight">{item.title}</h3>
                  <p className="text-landing-green text-sm leading-relaxed">
                    {item.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </section>

        {/* Alternating bands */}
        <LandingBand
          sage
          black
          image={mascotPencilRun.url}
          imageAlt="Refify pencil mascot running"
          imageClassName="max-w-sm lg:max-w-[min(29vw,553px)]"
          title="Fast & Accurate Citations"
          body="create citations in seconds."
        />
        <LandingBand
          image={screenshotApp.url}
          imageAlt="Refify papers overview"
          imageSide="right"
          imageClassName="max-w-full rounded-[30px]"
          black
          title="Everything you need in one place"
          body="Collect, organise, annotate and cite your sources in academic writing."
        />
        <LandingBand
          sage
          black
          image={mascotCup.url}
          imageAlt="Refify mascot throwing away corrections"
          imageClassName="max-w-sm lg:max-w-[min(24vw,460px)]"
          title="No more late nights checking and correcting your references"
          titleClassName="lg:max-w-[64vw]"
          body="Create and customise citation styles to match your university's referencing guidelines."
        />
        <LandingBand
          image={screenshotSearchFill.url}
          imageAlt="Search and fill in Refify"
          imageSide="right"
          imageClassName="max-w-full"
          black
          title="Add citations with ease"
          body="Use Search & Fill to find complete reference details from a DOI, ISBN, URL, or plain text. Refify retrieves the available information and fills in the details for you."
        />
        <LandingBand
          sage
          black
          image={mascotCloud.url}
          imageAlt="Refify cloud mascot"
          imageClassName="max-w-sm lg:max-w-[min(21vw,404px)]"
          title="Built on trust"
          bodySize="md"
          body={'Everything you create in Refify is securely stored in the cloud, so your references and papers are available wherever you work, whenever you need them.\nWe take your privacy seriously. We never sell your data, and you can export your entire library whenever you want. Refify is fully GDPR-compliant.'}
        />
        <LandingBand
          image={stylesPills.url}
          imageAlt="Citation styles: APA, Harvard, MLA, Chicago, Vancouver, IEEE"
          imageClassName="max-w-sm lg:max-w-[min(29vw,554px)]"
          imageSide="right"
          black
          title="Choose from 10,000+ citation styles"
          titleClassName="lg:max-w-[38vw]"
          body="Customise existing styles or create your own using Refify's visual template builder to match your referencing requirements."
        />
        <LandingBand
          sage
          image={mascotPencil.url}
          imageAlt="Refify pencil mascot"
          imageClassName="max-w-sm md:max-w-md lg:max-w-[min(30vw,571px)]"
          title="Create and control reference styles with precision."
          bodySize="md"
          body="Don't settle for &quot;good enough&quot; referencing. Take full control of your formatting, down to every space and punctuation mark. Build, customise, and automate citation styles to meet your institution's exact requirements."
        />
        <LandingBand
          image={templateBuilderGraphic.url}
          imageAlt="Template builder and citation panel"
          imageSide="right"
          imageClassName="max-w-full"
          title="Template Builder"
          bodySize="md"
          body="Choose from thousands of existing citation styles, customise a template to match your university's guidelines, or create your own from scratch using Refify's visual template builder."
        />
        <LandingBand
          sage
          black
          image={mascotPaper.url}
          imageAlt="Refify paper mascot"
          imageClassName="max-w-sm md:max-w-md lg:max-w-[min(26vw,502px)]"
          title="For new students and experienced academics."
          body="Refify offers an easy-to-use tool with advanced flexibility for those who need it."
        />

        {/* Mid CTA */}
        <CtaSection title="Try for yourself!" />

        {/* Features with tutorial videos */}
        <FeaturesSection features={features} />

        {/* FAQ */}
        <FaqSection />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify({
              '@context': 'https://schema.org',
              '@type': 'FAQPage',
              mainEntity: faqItems.map((item) => ({
                '@type': 'Question',
                name: item.q,
                acceptedAnswer: { '@type': 'Answer', text: item.a },
              })),
            }),
          }}
        />

        {/* Final CTA */}
        <CtaSection title="Ready to simplify your references?" dark />
      </main>

      {/* Footer */}
      <footer className="bg-landing-deep text-white py-12">
        <div className="container mx-auto px-6">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={footerLogo} alt="Refify logo" className="h-7 w-auto object-contain" />
              <p className="text-sm text-white/80 mt-4">
                Interactive Referencing System
              </p>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">Product</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><button onClick={() => scrollToSection('features')} className="hover:text-white transition-colors">Features</button></li>
                <li><Link to="/pricing" className="hover:text-white transition-colors">Pricing</Link></li>
                <li><Link to="/auth" className="hover:text-white transition-colors">Get started</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">Resources</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><Link to="/guides" className="hover:text-white transition-colors">Guides</Link></li>
                <li><Link to="/about" className="hover:text-white transition-colors">About</Link></li>
              </ul>
            </div>
            <div>
              <h3 className="font-bold mb-4 text-base">Contact</h3>
              <ul className="space-y-2 text-sm text-white/80">
                <li><a href="mailto:support@refify.app" className="hover:text-white transition-colors">support@refify.app</a></li>
                <li className="pt-2">
                  <div className="flex items-center gap-3">
                    <a
                      href="https://www.instagram.com/refify.app/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Refify on Instagram"
                      className="hover:text-white transition-colors"
                      onClick={openExternalLink('https://www.instagram.com/refify.app/')}
                    >
                      <Instagram className="w-5 h-5" />
                    </a>
                    <a
                      href="https://www.facebook.com/share/1Dr6pj5T7q/"
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Refify on Facebook"
                      className="hover:text-white transition-colors"
                      onClick={openExternalLink('https://www.facebook.com/share/1Dr6pj5T7q/')}
                    >
                      <Facebook className="w-5 h-5" />
                    </a>
                  </div>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-white/80 text-sm">© 2026 Refify. All rights reserved.</p>
            <div className="flex gap-6 text-sm text-white/80">
              <Link to="/privacy" className="hover:text-white transition-colors">Privacy</Link>
              <Link to="/refund" className="hover:text-white transition-colors">Refunds</Link>
              <Link to="/terms" className="hover:text-white transition-colors">Terms</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}
